/* eslint-disable complexity */
import { FIELD_QUANTITY } from '@components/dynamic-input-fields/src/utils';
import { formatBookingHolderFromBackend } from '@components/forms/utils/format-booking-formFields-from-backend';
import { formatChannelsToBackend } from '@components/forms/utils/format-channels-to-backend';
import { formatFormFieldsToBackend } from '@components/forms/utils/format-form-fields-to-backend';
import { formatItemsPerTransactionToBackend } from '@components/forms/utils/format-items-per-transaction-to-backend';
import { formatPriceToBackend } from '@components/forms/utils/format-price-to-backend';
import { formatTicketHolderFromBackend } from '@components/forms/utils/format-ticket-formFields-from-backend';
import {
  CHANNELS_SALES_QUOTA_CHANNEL,
  CHANNELS_SALES_QUOTA_UNLIMITED,
} from '@components/input-select-channels/src/input-select-channels.type';
import { ADMISSION_TYPE_BOTTLE_SERVICE, BOOKING_FORM_FIELDS, TICKET_HOLDER_FORM_FIELDS } from '@domain/admission';
import { CreateAdmissionPresetRequest, Translations } from '@domain/preset';
import { calculateDirittoPrevendita } from '@utils/calculateDirittoPrevendita/calculateDirittoPrevendita';
import { clearEmptyValues } from '@utils/clear-empty-fields';

import { DefaultValuesProps, Form, GetTicketFromFormProps } from './create-preset.type';

export const getDefaultValues = ({ locale, preset, formFieldsPresets, type, company }: DefaultValuesProps): Form => {
  const formattedTicketHolderFormFields = formatTicketHolderFromBackend({
    formFields: formFieldsPresets.ticketHolderFormFieldPresets,
    offerTicketHolderFormFields: preset?.ticketHolderFormFields,
  });
  const formattedBookingFormFields = formatBookingHolderFromBackend({
    formFields: formFieldsPresets.bookingFormFieldPresets,
    type,
    offerBookingFormFields: preset?.bookingFormFields,
    company,
  });

  const ddp: string = preset?.italyRegulation?.dirittoPrevendita?.toString() ?? '';

  return {
    price: preset?.price.amount.toString() ?? '',
    quantity: preset ? (preset.quantity == null ? '' : preset.quantity.toString()) : '',
    guestCapacity: preset ? ('guestCapacity' in preset ? preset.guestCapacity.toString() : '') : '',
    name: preset?.name ?? {
      en: '',
      ca: '',
      es: '',
      pt: '',
      it: '',
      fr: '',
      de: '',
    },
    description: preset?.description ?? {
      en: '',
      ca: '',
      es: '',
      pt: '',
      it: '',
      fr: '',
      de: '',
    },
    itemsPerTransaction: {
      min: preset?.itemsPerTransaction.min.toString() ?? '1',
      max: preset?.itemsPerTransaction.max.toString() ?? '',
    },
    channels: [],
    descriptionLanguage: locale,
    accessCode: preset?.accessCode ?? null,
    isPrivate: preset?.settings.isPrivate ?? false,
    isNominative: preset?.settings.isNominative ?? false,
    isChannelsDirty: false,
    isForAllChannels: !!preset?.accessCode || !!preset?.settings.channelDefaultMaxQuantity,
    ticketHolderFormFields: formattedTicketHolderFormFields,
    bookingFormFields: formattedBookingFormFields,
    color: preset?.color ?? '',
    ddp,
    drinksIncluded: preset?.drinksIncluded?.toString() ?? '',
    ticketsLeftThreshold: preset?.ticketsLeftThreshold?.toString() ?? '',
    acceptDownPayment: !!preset?.price.offlinePrice,
    salesQuota: preset?.settings.channelDefaultMaxQuantity
      ? CHANNELS_SALES_QUOTA_CHANNEL
      : CHANNELS_SALES_QUOTA_UNLIMITED,
    onlinePrice: preset?.price.onlinePrice?.toString() ?? '',
    vatRate: preset && 'vatRate' in preset ? preset.vatRate?.toString() ?? '' : '',
  };
};

// eslint-disable-next-line complexity
export const getTicketFromForm = ({
  form,
  offer,
  type,
  currency,
  salesQuota,
  companyClubId,
  admissionChannels,
  defaultTicketsLeftThreshold,
}: GetTicketFromFormProps): CreateAdmissionPresetRequest | null => {
  let defaultMaxQuantity = null;
  const areAllSelected =
    ('isForAllChannels' in form && !form.isForAllChannels) || !!admissionChannels?.every((option) => option.checked);
  const isSettingsEdited = 'status' in form || 'isPrivate' in form || 'isNominative' in form || !!admissionChannels;
  const offerIsNominative = form.isNominative ?? offer?.settings.isNominative;
  if (form.limitAllChannels && areAllSelected) defaultMaxQuantity = +form.limitAllChannels;

  let preset: CreateAdmissionPresetRequest = {
    ...(offer ? undefined : { companyClubId, onlinePaymentPercentage: 100 }),
    ...(isSettingsEdited && {
      settings: {
        ...('isPrivate' in form && { isPrivate: form.isPrivate }),
        ...(admissionChannels && {
          isForAllChannels: salesQuota === CHANNELS_SALES_QUOTA_CHANNEL ? false : areAllSelected,
        }),
        ...('isNominative' in form && { isNominative: form.isNominative }),
        ...(admissionChannels && { channelDefaultMaxQuantity: defaultMaxQuantity }),
      },
    }),
    ...('color' in form && { color: form.color ?? null }),
    ...(('price' in form || 'onlinePrice' in form) && {
      price: formatPriceToBackend({
        price: form.price ?? offer?.price.amount,
        downPayment: form.onlinePrice,
        currency,
        type,
      }),
    }),
    ...(admissionChannels && {
      channels: formatChannelsToBackend({
        channelOptions: admissionChannels,
        areAllSelected,
        salesQuota,
        limitAllChannels: form.limitAllChannels ?? '',
      }),
    }),
    ...(FIELD_QUANTITY in form && { quantity: form.quantity ? Number(form.quantity) : null }),
    ...('name' in form && { name: clearEmptyValues((form.name as Translations) ?? {}) }),
    ...('description' in form && { description: clearEmptyValues(form.description ?? {}) }),
    ...('itemsPerTransaction' in form && {
      itemsPerTransaction: formatItemsPerTransactionToBackend({
        min: form.itemsPerTransaction?.min ?? '1',
        max: form.itemsPerTransaction?.max,
        quantity: Number(form.quantity ?? offer?.quantity),
        type,
      }),
    }),
    ...(BOOKING_FORM_FIELDS in form && {
      bookingFormFields: formatFormFieldsToBackend({ formFields: form.bookingFormFields ?? [] }),
    }),
    ...(('isNominative' in form || TICKET_HOLDER_FORM_FIELDS in form) && {
      ticketHolderFormFields: offerIsNominative
        ? formatFormFieldsToBackend({ formFields: form.ticketHolderFormFields ?? [] })
        : [],
    }),
    ...('accessCode' in form && { accessCode: form.accessCode === '' ? null : form.accessCode }),
    ...('vatRate' in form && {
      vatRate: form.vatRate ? Number(form.vatRate) : null,
    }),
  };

  if (type === ADMISSION_TYPE_BOTTLE_SERVICE && form.guestCapacity) {
    preset = { ...preset, guestCapacity: Number(form.guestCapacity) };
  }

  if (form.ddp || form.ddp === '') {
    const dirittoPrevendita = calculateDirittoPrevendita(form.ddp);
    preset = {
      ...preset,
      italyRegulation: {
        dirittoPrevendita,
      },
    };
  }

  if (form.drinksIncluded) preset = { ...preset, drinksIncluded: Number(form.drinksIncluded) };

  if ('ticketsLeftThreshold' in form) {
    preset = {
      ...preset,
      ticketsLeftThreshold:
        form.ticketsLeftThreshold === '' ? defaultTicketsLeftThreshold : Number(form.ticketsLeftThreshold),
    };
  }

  return preset;
};
